import { useEffect, useState } from 'react';

/**
 * Generic matchMedia hook — the single mechanism behind all responsive
 * behavior gates (see useIsMobile.js for the canonical queries).
 *
 * SSR/jest-safe: initializes to `false` when matchMedia is unavailable.
 * Live: re-evaluates on media-query 'change' (rotation, window resize,
 * devtools emulation toggle).
 */
export function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const mql = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);
    // Sync in case the query changed between render and effect.
    setMatches(mql.matches);
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    // Safari < 14 fallback
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [query]);

  return matches;
}

export default useMediaQuery;
